import { useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '@/hooks/useToast';
import { useConfirm } from '@/hooks/useConfirm';
import { useModal } from '@/hooks/useModal';
import { Button, Modal } from '@/components/ui';
import { preferencesService } from '@/services/api';
import { KEYS, KEY_GROUPS, USER_EDITABLE_KEYS } from '@/features/apiKeys/catalog';
import type { ApiKeyTestResult, ApolloEndpointResult, OrgKeyName, PreferencesData } from '@/types';
import '@/styles/pages/settings.css';

const APOLLO_ENDPOINTS = [
  { endpoint: '/api/v1/organizations/search', description: 'Required for Apollo Search.' },
  {
    endpoint: '/api/v1/organizations/enrich',
    description: 'Required for single company enrichment.',
  },
  { endpoint: '/api/v1/organizations/bulk_enrich', description: 'Required for bulk enrichment.' },
  {
    endpoint: '/api/v1/mixed_people/api_search',
    description: 'Required for contact/investor search.',
  },
  { endpoint: '/api/v1/people/match', description: 'Required for email matching/unlocking.' },
];

export default function ApiKeysTab() {
  const { success, error, info } = useToast();
  const confirm = useConfirm();
  const apolloModal = useModal();

  const [prefs, setPrefs] = useState<PreferencesData | null>(null);
  const [results, setResults] = useState<Partial<Record<OrgKeyName, ApiKeyTestResult>>>({});
  const [busy, setBusy] = useState<'update' | 'test' | 'clear' | null>(null);
  const [expandedKey, setExpandedKey] = useState<OrgKeyName | null>(null);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [apolloResults, setApolloResults] = useState<ApolloEndpointResult[] | null>(null);
  const [apolloTesting, setApolloTesting] = useState(false);

  useEffect(() => {
    preferencesService
      .getPreferences()
      .then(setPrefs)
      .catch(() => setPrefs(null));
  }, []);

  useEffect(() => {
    if (expandedKey) inputRef.current?.focus();
  }, [expandedKey]);

  const configured = useMemo(
    () => prefs?.apiKeyStatus ?? ({} as Record<OrgKeyName, boolean>),
    [prefs],
  );
  const source = prefs?.apiKeySource ?? {};

  const runTest = async () => {
    setBusy('test');
    try {
      const data = await preferencesService.testApiKeys();
      if (data.success) {
        setResults(data.testResults);
        const resultsMap = data.testResults;
        const keys = (Object.keys(resultsMap) as OrgKeyName[]).filter((key) =>
          USER_EDITABLE_KEYS.has(key),
        );
        const valid = keys.filter((k) => resultsMap[k]?.status === 'valid').length;
        const formatLine = (k: OrgKeyName) => {
          const name = k
            .replace(/_/g, ' ')
            .replace(/api key/i, '')
            .trim();
          const capName = name.charAt(0).toUpperCase() + name.slice(1);
          const r = resultsMap[k];
          const icon =
            r?.status === 'valid'
              ? '✅'
              : ['rate_limited', 'service_unavailable'].includes(r?.status || '')
                ? '⏳'
                : '❌';
          return `${icon} ${capName}: ${r?.message || 'Not tested'}`;
        };
        let summary = `${valid}/${keys.length} API keys are working properly.\n\n`;
        keys.forEach((k) => {
          summary += formatLine(k) + '\n';
        });
        success('API Keys Test Complete', summary, false);
      } else {
        error('API Keys Test Failed', data.message || 'Unknown error occurred');
      }
    } catch {
      error('API Keys Test Error', 'Failed to test API keys. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const runApolloEndpointTest = async () => {
    const ok = await confirm({
      title: 'Test all Apollo endpoints?',
      message:
        'This calls each Apollo endpoint individually to confirm it works. The search, enrich, ' +
        'bulk-enrich and people-match endpoints charge ~1 Apollo credit each on success, so a ' +
        'full run spends about 4 Apollo credits. Continue?',
      confirmText: 'Test endpoints (~4 credits)',
      cancelText: 'Cancel',
    });
    if (!ok) return;

    setApolloTesting(true);
    try {
      const data = await preferencesService.testApolloEndpoints();
      if (!data.configured) {
        info('Apollo not configured', 'Add an Apollo API key before testing its endpoints.');
        setApolloResults(null);
        return;
      }
      setApolloResults(data.endpoints);
      const allOk = data.workingCount === data.totalCount;
      const summary = `${data.workingCount}/${data.totalCount} Apollo endpoints working.`;
      if (allOk) success('Apollo Endpoints Test Complete', summary);
      else info('Apollo Endpoints Test Complete', summary);
    } catch {
      error('Apollo Endpoints Test Error', 'Failed to test Apollo endpoints. Please try again.');
    } finally {
      setApolloTesting(false);
    }
  };

  const testSingleKey = async (keyName: OrgKeyName, keyValue?: string) => {
    setBusy('test');
    try {
      const data = await preferencesService.testSingleApiKey(keyName, keyValue);
      if (data.success) {
        if (!keyValue) {
          setResults((prev) => ({ ...prev, [keyName]: data.testResults[keyName] }));
        }
        const res = data.testResults[keyName];
        if (res.status === 'valid') {
          success('Test Successful', res.message);
        } else if (
          ['credits_exhausted', 'model_unavailable', 'quota_exceeded', 'rate_limited'].includes(
            res.status,
          )
        ) {
          info('Test Warning', res.message);
        } else {
          error('Test Failed', res.message);
        }
      } else {
        error('Test Failed', data.message || 'Unknown error occurred');
      }
    } catch {
      error('Test Error', 'Failed to test API key. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const updateKey = async (keyName: OrgKeyName) => {
    const v = inputValue.trim();
    if (!v) {
      error('Empty API Key', 'Please enter a valid API key to save.');
      return;
    }
    setBusy('update');
    try {
      const data = await preferencesService.updateApiKeys({ [keyName]: v });
      if (data.success) {
        setPrefs((p) =>
          p
            ? {
                ...p,
                apiKeyStatus: { ...p.apiKeyStatus, [keyName]: true },
                apiKeySource: { ...p.apiKeySource, [keyName]: 'user' },
              }
            : p,
        );
        setResults((r) => {
          const next = { ...r };
          delete next[keyName];
          return next;
        });
        success('API Key Saved', data.message || 'API key updated successfully.');
        setExpandedKey(null);
        setInputValue('');
        // Only verify the key that was just saved — full suite is "Test All API Keys".
        setTimeout(() => void testSingleKey(keyName), 600);
      } else {
        error('Update Failed', data.message || 'Failed to update API key.');
      }
    } catch {
      error('Error', 'An error occurred while updating the API key.');
    } finally {
      setBusy(null);
    }
  };

  const clearKey = async (keyName: OrgKeyName) => {
    const ok = await confirm({
      title: 'Clear API Key',
      message: 'Are you sure you want to clear this API key?',
      confirmText: 'Clear Key',
      cancelText: 'Cancel',
    });
    if (!ok) return;

    setBusy('clear');
    try {
      const payload: Partial<Record<OrgKeyName, string>> = { [keyName]: '' };
      const data = await preferencesService.updateApiKeys(payload);
      if (data.success) {
        setPrefs((p) => {
          if (!p) return p;
          const nextSource = { ...p.apiKeySource };
          delete nextSource[keyName];
          return {
            ...p,
            apiKeyStatus: { ...p.apiKeyStatus, [keyName]: false },
            apiKeySource: nextSource,
          };
        });
        setResults((r) => {
          const next = { ...r };
          delete next[keyName];
          return next;
        });
        success('API Key Cleared', data.message || 'The API key has been removed.');
      } else {
        error('Clear Failed', data.message || 'Failed to clear API key.');
      }
    } catch {
      error('Error', 'An error occurred while clearing API key.');
    } finally {
      setBusy(null);
    }
  };

  const clearAllKeys = async () => {
    const ok = await confirm({
      title: 'Clear all API keys?',
      message:
        'This will permanently remove every Data Source API key you have saved. Organization defaults are not affected, but your personal keys cannot be recovered.',
      confirmText: 'Clear All Keys',
      cancelText: 'Cancel',
      danger: true,
    });
    if (!ok) return;

    setBusy('clear');
    try {
      const data = await preferencesService.clearApiKeys();
      if (data.success) {
        setPrefs((p) => {
          if (!p) return p;
          const apiKeyStatus = { ...p.apiKeyStatus };
          (Object.keys(apiKeyStatus) as OrgKeyName[]).forEach((k) => {
            if (!USER_EDITABLE_KEYS.has(k)) return;
            apiKeyStatus[k] = false;
          });
          return { ...p, apiKeyStatus, apiKeySource: {} };
        });
        setResults({});
        setExpandedKey(null);
        setInputValue('');
        success('API Keys Cleared', data.message || 'All API keys have been cleared.');
      } else {
        error('Clear Failed', data.message || 'Failed to clear API keys.');
      }
    } catch {
      error('Error', 'An error occurred while clearing API keys.');
    } finally {
      setBusy(null);
    }
  };

  const toggleExpand = (keyName: OrgKeyName) => {
    if (expandedKey === keyName) {
      setExpandedKey(null);
    } else {
      setExpandedKey(keyName);
      setInputValue('');
    }
  };

  const getStatusIcon = (keyName: OrgKeyName) => {
    const isConfig = configured[keyName];
    const res = results[keyName];

    if (res) {
      if (res.status === 'valid') return <i className="bi bi-shield-check" title={res.message} />;
      if (
        [
          'quota_exceeded',
          'credits_exhausted',
          'model_unavailable',
          'rate_limited',
          'timeout',
        ].includes(res.status)
      )
        return <i className="bi bi-shield-exclamation text-warning" title={res.message} />;
      return <i className="bi bi-shield-x text-danger" title={res.message} />;
    }

    if (isConfig) {
      return <i className="bi bi-check-lg" title="Configured" />;
    }
    return <i className="bi bi-dash" title="Not configured" />;
  };

  return (
    <div className="settings-card settings-api-keys-section">
      <div className="settings-card__head">
        <div>
          <h2 className="settings-card__title">Data Sources</h2>
          <p className="settings-card__sub">
            Optional — each enabled source adds more company discovery coverage.
          </p>
        </div>
        <div className="settings-card__actions settings-card__actions--head">
          <Button
            variant="danger"
            pill
            className="settings-clear-all-btn"
            onClick={clearAllKeys}
            disabled={busy !== null}
          >
            {busy === 'clear' ? 'Clearing...' : 'Clear All Keys'}
          </Button>
          <Button variant="primary" pill onClick={runTest} disabled={busy === 'test'}>
            {busy === 'test' ? 'Testing...' : 'Test All API Keys'}
          </Button>
        </div>
      </div>
      <div className="settings-card__body">
        <div className="api-keys-tab">
          <div className="api-keys-groups">
            {KEY_GROUPS.filter((group) => group.category === 'data_source').map((group) => (
              <div key={group.title} className="api-keys-group">
                <div className="api-keys-list">
                  {group.keys.map((keyName) => {
                    const meta = KEYS[keyName as OrgKeyName];
                    if (!meta) return null;
                    const kn = keyName as OrgKeyName;
                    const isExpanded = expandedKey === kn;
                    const src = source[kn];

                    return (
                      <div
                        key={keyName}
                        className={`api-key-row-wrapper${isExpanded ? ' expanded' : ''}`}
                      >
                        <div className="api-key-row">
                          <div className="api-key-info">
                            <span
                              className={`api-key-status-icon ${configured[kn] ? 'status-configured' : 'status-empty'}`}
                            >
                              {getStatusIcon(kn)}
                            </span>
                            <div className="api-key-details">
                              <div className="api-key-header">
                                <span className="api-key-name">{meta.label}</span>
                                <div className="api-key-badges">
                                  {meta.required && (
                                    <span className="settings-badge settings-badge--required">
                                      REQUIRED
                                    </span>
                                  )}
                                  {src === 'org' ? (
                                    <span className="settings-badge settings-badge--app">
                                      ORG DEFAULT
                                    </span>
                                  ) : src === 'user' ? (
                                    <span className="settings-badge settings-badge--user">
                                      YOUR KEY
                                    </span>
                                  ) : null}
                                </div>
                                {meta.apolloInfo && (
                                  <button
                                    type="button"
                                    className="btn-unstyled settings-icon-btn"
                                    onClick={apolloModal.show}
                                    title="View required endpoints"
                                    aria-label="View required Apollo endpoints"
                                  >
                                    <i className="bi bi-info-circle" />
                                  </button>
                                )}
                              </div>
                              <div className="api-key-status-text">
                                {results[kn]?.message ? (
                                  <>
                                    <i
                                      className={`bi ${
                                        results[kn]?.status === 'valid'
                                          ? 'bi-check-circle api-key-status-ok'
                                          : 'bi-exclamation-circle api-key-status-warn'
                                      }`}
                                    />
                                    {results[kn]?.message}
                                  </>
                                ) : configured[kn] ? (
                                  <>
                                    <i className="bi bi-check-circle api-key-status-ok" />
                                    Configured
                                  </>
                                ) : (
                                  <>
                                    <i className="bi bi-dash-circle api-key-status-empty" />
                                    Not configured
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="api-key-actions">
                            <button
                              type="button"
                              className="btn-unstyled settings-icon-btn"
                              onClick={() => testSingleKey(kn)}
                              title="Test Current Key"
                              aria-label={`Test ${meta.label}`}
                              disabled={busy === 'test' || (!configured[kn] && src !== 'user')}
                            >
                              <i className="bi bi-play-fill" />
                            </button>
                            <button
                              type="button"
                              className="btn-unstyled settings-icon-btn settings-icon-btn--danger"
                              onClick={() => clearKey(kn)}
                              title="Clear Key"
                              aria-label={`Clear ${meta.label}`}
                              disabled={busy === 'clear' || (!configured[kn] && src !== 'user')}
                            >
                              <i className="bi bi-trash" />
                            </button>
                            <Button
                              variant="secondary"
                              size="sm"
                              pill
                              className="api-key-update-btn"
                              onClick={() => toggleExpand(kn)}
                            >
                              Update
                              <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`} />
                            </Button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="api-key-expand-content">
                            <input
                              ref={inputRef}
                              type="text"
                              className="api-key-input"
                              placeholder={`Paste your ${meta.label} here`}
                              value={inputValue}
                              onChange={(e) => setInputValue(e.target.value)}
                            />
                            <div className="api-key-expand-actions">
                              <Button
                                variant="primary"
                                pill
                                onClick={() => updateKey(kn)}
                                disabled={busy === 'update' || !inputValue.trim()}
                              >
                                {busy === 'update' ? 'Saving...' : 'Save'}
                              </Button>
                              <Button
                                variant="secondary"
                                pill
                                disabled={busy === 'test' || !inputValue.trim()}
                                onClick={() => testSingleKey(kn, inputValue.trim())}
                              >
                                {busy === 'test' ? 'Testing...' : 'Test key'}
                              </Button>
                              <Button variant="secondary" pill onClick={() => toggleExpand(kn)}>
                                Cancel
                              </Button>
                            </div>
                            <p className="api-key-expand-hint">
                              <i className="bi bi-info-circle" />
                              Keys are stored encrypted per-user and used only for your runs. They
                              are never shared or logged.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Modal
        open={apolloModal.open}
        onClose={() => {
          setApolloResults(null);
          apolloModal.close();
        }}
        title="Required Apollo.io API Endpoints"
        size="md"
      >
        <p className="api-key-expand-hint">
          The following endpoints must be enabled when creating your Apollo.io API key:
        </p>
        <div className="settings-apollo-list">
          {APOLLO_ENDPOINTS.map((item, i) => {
            // Match a probe result by its short path (e.g. "organizations/search") being a
            // substring of the full endpoint URL shown here.
            const res = apolloResults?.find((r) => item.endpoint.includes(r.endpoint));
            return (
              <div className="settings-apollo-item" key={item.endpoint}>
                <span>{i + 1}.</span>
                <code>{item.endpoint}</code>
                <span>— {item.description}</span>
                {res && (
                  <span
                    className={`settings-apollo-status ${
                      res.status === 'working' ? 'text-success' : 'text-danger'
                    }`}
                    title={res.message}
                  >
                    <i
                      className={`bi ${
                        res.status === 'working' ? 'bi-check-circle' : 'bi-x-circle'
                      }`}
                    />
                    {res.status === 'working' ? 'Working' : 'Failed'}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <div className="settings-card__actions">
          <Button variant="secondary" pill onClick={runApolloEndpointTest} disabled={apolloTesting}>
            {apolloTesting ? 'Testing…' : 'Test all endpoints'}
          </Button>
          <Button
            variant="primary"
            pill
            onClick={() => {
              setApolloResults(null);
              apolloModal.close();
            }}
          >
            Got it
          </Button>
        </div>
      </Modal>
    </div>
  );
}
